import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../../components/AthenaPricingEngine/page-header";
import { Panel } from "../../../components/AthenaPricingEngine/panel";
import { FilterBar } from "../../../components/AthenaPricingEngine/filter-bar";
import { SegmentsTable } from "../../../components/AthenaPricingEngine/segments-table";
import { Skeleton } from "../../../ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group";
import { GroupedBarChart } from "../../../components/AthenaPricingEngine/charts/grouped-bar-chart";
import {
  SEGMENT_LEVELS,
  useSegmentsSummary,
  type RavBasis,
  type SegmentLevel,
  type SegmentNode,
} from "../lib/api/segments";
import { useIndustries, useOffices } from "../lib/api/reference";

const GRADE_OPTIONS = ["A", "B1", "B2", "B3"];

const ALL_OFFICES_LABEL = "All Offices";
const ALL_INDUSTRIES_LABEL = "All Industry";
const ALL_GRADES_LABEL = "All Ratings";

/** Keeps only the branches that reach a node matching every supplied filter. */
function filterTree(
  rows: SegmentNode[],
  filters: {
    grade?: string | undefined;
    office?: string | undefined;
    industry?: string | undefined;
  },
): SegmentNode[] {
  const { grade, office, industry } = filters;
  const out: SegmentNode[] = [];
  for (const row of rows) {
    // `!row.office` bypasses the check the same way the industry check below
    // does -- an Industry-grain row has no office of its own to compare
    // against, so an Office filter (only ever passed in on tabs where it
    // applies) must not reject every row outright.
    if (office && row.office && row.office !== office) continue;
    const subRows = row.subRows ? filterTree(row.subRows, filters) : undefined;
    const industryOk = !industry || !row.industry || row.industry === industry;
    if (!industryOk) continue;
    const selfMatches = grade ? row.grade === grade : true;
    if (grade && !selfMatches && !(subRows && subRows.length)) continue;
    if (industry && !row.industry && !(subRows && subRows.length)) continue;
    const kept: SegmentNode = { ...row };
    if (subRows && subRows.length) kept.subRows = subRows;
    else delete kept.subRows;
    out.push(kept);
  }
  return out;
}

export default function Segments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchGrade = searchParams.get("grade") ?? undefined;
  const grade = searchGrade && GRADE_OPTIONS.includes(searchGrade) ? searchGrade : undefined;
  const office = searchParams.get("office") ?? undefined;
  const industry = searchParams.get("industry") ?? undefined;

  const officesQuery = useOffices();
  const offices = officesQuery.data ?? [];
  const industriesQuery = useIndustries();
  const industries = industriesQuery.data ?? [];

  /** Writes (or clears, for the "All ..." sentinel) one filter's URL param --
   * `filterTree` above already reads `grade`/`office`/`industry` from the URL,
   * this just gives that a real on-page control instead of deep-link-only
   * access. */
  function setFilterParam(key: "grade" | "office" | "industry", value: string, allLabel: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === allLabel) next.delete(key);
      else next.set(key, value);
      return next;
    });
  }

  // Seeded once from the deep-link's own ?level= (e.g. Manage Targets' "View in
  // Segments" links) rather than always defaulting to Industry -- otherwise a
  // link from the Office-led ladder landed on the Industry tab, where the
  // Office filter it also carried forward doesn't apply and was silently
  // dropped. Read once via the lazy initializer, same as any other deep-link
  // default on this page; the toggle group still switches freely afterward.
  const [level, setLevel] = useState<SegmentLevel>(() => {
    const raw = searchParams.get("level");
    return SEGMENT_LEVELS.find((l) => l.toLowerCase() === raw?.toLowerCase()) ?? "Industry";
  });
  const [basis, setBasis] = useState<RavBasis>("projected");
  const [focus, setFocus] = useState<{ id: string; nonce: number } | null>(null);
  const { data, isLoading, isError, error } = useSegmentsSummary(level, { ravBasis: basis });

  // Office rows have no industry of their own and Industry rows have no office
  // of their own (see segments.ts) -- applying the "wrong axis" filter for the
  // current grain would compare a real name against a field the row doesn't
  // carry and drop every row. Only pass through the filter that actually
  // applies to what's on screen; Portfolio shows both grains (offices nested
  // under one portfolio total) so both apply there.
  const effectiveOffice = level === "Industry" ? undefined : office;
  const effectiveIndustry = level === "Office" ? undefined : industry;

  const rows = useMemo(() => {
    if (!data) return [];
    const anyFilter = grade || effectiveOffice || effectiveIndustry;
    return anyFilter
      ? filterTree(data.rows, { grade, office: effectiveOffice, industry: effectiveIndustry })
      : data.rows;
  }, [data, grade, effectiveOffice, effectiveIndustry]);

  /** Net RAV against approved target net margin, at whichever grain is showing. */
  const comparison = useMemo(
    () =>
      rows.map((row) => ({
        id: row.id,
        segment: row.industry ?? row.office ?? row.id,
        netRav: Number(row.netRav.toFixed(2)),
        targetNetMargin: Number(row.targetNetMargin.toFixed(2)),
      })),
    [rows],
  );

  return (
    <div>
      <PageHeader title="Segments" subtitle="By industry, office, or portfolio" />

      <div className="mt-8 flex flex-wrap items-end justify-between gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Grain
          </span>
          <ToggleGroup
            type="single"
            value={level}
            onValueChange={(value) => value && setLevel(value as SegmentLevel)}
            className="border border-border bg-secondary"
          >
            {SEGMENT_LEVELS.map((option) => (
              <ToggleGroupItem key={option} value={option} className="h-9 px-4 text-sm">
                {option}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            RAV Basis
          </span>
          <ToggleGroup
            type="single"
            value={basis}
            onValueChange={(value) => value && setBasis(value as RavBasis)}
            className="border border-border bg-secondary"
          >
            <ToggleGroupItem value="realized" className="h-8 px-3 text-xs">
              Realized
            </ToggleGroupItem>
            <ToggleGroupItem value="projected" className="h-8 px-3 text-xs">
              Projected
            </ToggleGroupItem>
          </ToggleGroup>
        </label>
      </div>

      {!isLoading && !isError && comparison.length ? (
        <Panel
          className="mt-6"
          title="Net RAV Against Target Net Margin"
          description="What each segment actually keeps after commission, against the approved target. Select a bar to open that segment in the table below."
        >
          <GroupedBarChart
            data={comparison}
            categoryKey="segment"
            series={[
              // Same rule as State of Play, mapped onto this chart's equivalent pair:
              // the TARGET is Athena yellow (as Required Yield is there) and the
              // ACHIEVED figure is Navitas blue (as Actual Yield is). Left the other
              // way round, yellow would mean "target" on State of Play and "achieved"
              // here, which is worse than either choice on its own.
              { dataKey: "netRav", name: "Net RAV", color: "var(--navitas-blue)" },
              {
                dataKey: "targetNetMargin",
                name: "Target Net Margin",
                color: "var(--athena-yellow)",
              },
            ]}
            height={Math.max(300, comparison.length * 18 + 80)}
            onBarClick={(row) => setFocus({ id: row.id, nonce: Date.now() })}
          />
        </Panel>
      ) : null}

      <Panel
        className="mt-6"
        title="Segment Economics"
        description={`${level} grain · ${basis === "projected" ? "Projected" : "Realized"} net RAV · expand a row to drill into ${level === "Industry" ? "grade" : "the next level"}`}
      >
        <FilterBar
          className="mb-5"
          filters={[
            // Office only means something on rows that actually carry an office
            // (Office and Portfolio grain) -- hidden on Industry, which has none.
            level !== "Industry"
              ? {
                  id: "office",
                  label: "Office Name",
                  value: office ?? ALL_OFFICES_LABEL,
                  options: [ALL_OFFICES_LABEL, ...offices.map((o) => o.officeName)],
                  onChange: (value) => setFilterParam("office", value, ALL_OFFICES_LABEL),
                }
              : null,
            // Industry only means something on rows that actually carry an
            // industry (Industry and Portfolio grain) -- hidden on Office.
            level !== "Office"
              ? {
                  id: "industry",
                  label: "Industry",
                  value: industry ?? ALL_INDUSTRIES_LABEL,
                  options: [ALL_INDUSTRIES_LABEL, ...industries],
                  onChange: (value) => setFilterParam("industry", value, ALL_INDUSTRIES_LABEL),
                }
              : null,
            {
              id: "grade",
              label: "Grade",
              value: grade ?? ALL_GRADES_LABEL,
              options: [ALL_GRADES_LABEL, ...GRADE_OPTIONS],
              onChange: (value) => setFilterParam("grade", value, ALL_GRADES_LABEL),
            },
          ].filter((f): f is NonNullable<typeof f> => f !== null)}
        />
        {isError ? (
          <div
            role="alert"
            className="border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            Could not load segment economics. {error instanceof Error ? error.message : ""}
          </div>
        ) : isLoading || !data ? (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <SegmentsTable rows={rows} basis={basis} focus={focus} />
        )}
      </Panel>
    </div>
  );
}
